import React from 'react';
import {
  Card,
  Heading,
  Flex,
  Icon,
  Text,
  Link as RadixLink,
  Button,
  Badge,
  Tooltip,
} from '@scale/scaleui-radix';
import {
  faCalendar,
  faVideo,
  faTag,
  faEnvelope,
  faBriefcase,
} from '@fortawesome/pro-regular-svg-icons';
import { contructGCalLink, formatSessionDate, isPastSession } from '../helpers';
import {
  GetCollectionSession,
  ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY,
} from '@scale/shared/webinarSessions/types';
import WebinarDetailRecordings from './WebinarDetailRecordings';
import { uniqBy } from 'lodash';
import { MoreHostsDialog } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/WebinarCollectionsTable';
import CopyButton from './CopyButton';

interface WebinarDetailSessionProps {
  session: GetCollectionSession;
}

const WebinarDetailSession: React.FC<WebinarDetailSessionProps> = ({ session }) => {
  const hostKey = ZOOM_SERVICE_ACCOUNT_TO_HOST_KEY[session.zoomMeeting.host] ?? 'Unavailable';
  const elapsed = isPastSession(session);

  const uniqHosts = uniqBy(session.hostedBy, '_id');

  const isRemoAdminOrOutlierAdmin =
    window.location.pathname.includes('remoadmin') ||
    window.location.pathname.includes('outlieradmin');

  return (
    <Card variant="surface" className="p-5 flex-shrink-0">
      <Heading size="4" className="mb-3">
        Session details
      </Heading>
      <Flex gap="2" justify="between">
        <Flex direction="column" gap="3">
          {!elapsed && (
            <>
              <div className="flex items-center gap-2">
                <Icon color="neutral" icon={faCalendar} size="2" label="calendar" />
                <Text size="2" color="neutral">
                  {formatSessionDate(session.eventStart)}
                </Text>
                <Tooltip content="Only adds this singular session to your Google Calendar">
                  <RadixLink
                    href={contructGCalLink(session)}
                    size="2"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Text size="2">Add to calendar</Text>
                  </RadixLink>
                </Tooltip>
              </div>
              <div className="flex items-center gap-2">
                <Icon color="neutral" icon={faVideo} size="2" label="Zoom link" />
                <Button size="1" variant="solid" color="accent" asChild>
                  <RadixLink href={session.zoomMeeting.joinUrl} target="_blank">
                    Join zoom
                  </RadixLink>
                </Button>
                <CopyButton text={session.zoomMeeting.joinUrl} label="Zoom link" />
                <Text size="2" color="neutral">
                  Host key:
                </Text>
                <Badge size="1" color="neutral">
                  {hostKey}
                </Badge>
                <CopyButton text={hostKey} label="host key" />
              </div>
            </>
          )}
          {elapsed && <WebinarDetailRecordings session={session} />}
          <div className="flex items-center gap-2">
            <Icon color="neutral" icon={faTag} size="2" label="Session ID" />
            <Text size="2" color="neutral">
              Session id:
            </Text>
            <Badge size="1" color="neutral">
              {session._id}
            </Badge>
            <CopyButton text={session._id} label="Webinar ID" />
          </div>
          {session.project && (
            <div className="flex items-center gap-2">
              <Icon color="neutral" icon={faBriefcase} size="2" label="Project" />
              <Text size="2" color="neutral">
                Project id:
              </Text>
              <Badge size="1" color="neutral" asChild={!isRemoAdminOrOutlierAdmin}>
                {!isRemoAdminOrOutlierAdmin ? (
                  <RadixLink
                    href={`/corp/genai-ops-hub/projects/${session.project}`}
                    target="_blank"
                  >
                    {session.project}
                  </RadixLink>
                ) : (
                  session.project
                )}
              </Badge>
              <CopyButton text={session.project} label="Project ID" />
            </div>
          )}
        </Flex>
        <Flex direction="column" gap="3">
          <Text size="2" weight="medium">
            Owners
          </Text>
          {uniqHosts.slice(0, 2).map((host, i) => (
            <div className="flex items-center gap-2" key={i}>
              <Icon color="neutral" icon={faEnvelope} size="2" label="host" />
              <Text size="2" color="neutral">
                {host.email}
              </Text>
            </div>
          ))}
          <MoreHostsDialog hosts={uniqHosts} />
        </Flex>
      </Flex>
    </Card>
  );
};

export default WebinarDetailSession;
